import { useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BookText,
  Settings,
  LayoutGrid,
  ClipboardList,
} from "lucide-react";

import { cn } from "@/lib/cn";
import { resolveAbilityHashAlias } from "@/config/abilities";
import { clearApiTicket } from "@/api/auth";
import { isEmbeddedMode } from "@/lib/embed";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  exact?: boolean;
}

// `/projects`、`/reports` 和原「档案」在 M1 是占位（ERP contract 未冻 / 报告中心未建 /
// 供应商与合同主数据在 ERP 侧，平台无对应实体表）。
// 主导航只保留真实功能入口，避免给客户演示时误导。
const NAV: ReadonlyArray<NavItem> = [
  { to: "/", label: "工作台", icon: LayoutGrid, exact: true },
  { to: "/analysis-runs", label: "分析记录", icon: ClipboardList },
  { to: "/settings/rules", label: "规则库", icon: BookText },
  { to: "/settings", label: "设置", icon: Settings },
];

export function AppShell() {
  const loc = useLocation();
  const navigate = useNavigate();
  const embedded = isEmbeddedMode(loc.search);

  useEffect(() => {
    const target = resolveAbilityHashAlias(loc.hash);
    if (target && loc.pathname === "/") {
      navigate({ pathname: target, search: loc.search }, { replace: true });
    }
  }, [loc.hash, loc.pathname, loc.search, navigate]);

  useEffect(() => {
    if (!embedded) {
      clearApiTicket();
    }
  }, [embedded]);

  // 当用户在 /abilities/* 时,左侧栏不亮任何主项,顶部面包屑会显示位置
  return (
    <div className="min-h-screen bg-canvas text-ink">
      {!embedded && (
        <header className="sticky top-0 z-50 border-b border-stroke-1 bg-surface">
          <div className="mx-auto flex h-14 max-w-screen-2xl items-center gap-6 px-6">
            <NavLink to="/" className="flex items-center gap-2 font-semibold">
              <span className="grid h-8 w-8 place-items-center rounded-md bg-brand text-surface">
                AI
              </span>
              <span className="text-md">AI 评标工作台</span>
            </NavLink>

            <nav className="flex items-center gap-1">
              {NAV.map((item) => {
                const Icon = item.icon;
                const active = item.exact
                  ? loc.pathname === item.to
                  : loc.pathname.startsWith(item.to);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={cn(
                      "flex h-9 items-center gap-1.5 rounded-md px-3 text-sm",
                      "hover:bg-surface-2 transition-colors",
                      active && "bg-brand-soft text-brand-accent font-medium",
                    )}
                  >
                    <Icon size={16} />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </header>
      )}

      <main
        className={cn("mx-auto max-w-screen-2xl", embedded ? "px-3 py-3" : "px-6 py-8")}
      >
        <Outlet />
      </main>
    </div>
  );
}
